import { DomainError } from "./errors";

/**
 * Datas civis `AAAA-MM-DD` (vencimentos, vigências), sem hora nem fuso: a conta é feita em UTC sobre o dia do
 * calendário, então nunca escorrega um dia por causa do horário de Brasília. "Hoje" vem de `todayInSaoPaulo`.
 */
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;

/** `Date.UTC` trata os anos 0–99 como 1900–1999: `setUTCFullYear` não. */
function utcMs(y: number, mo: number, d: number): number {
  const t = new Date(0);
  t.setUTCFullYear(y, mo - 1, d);
  return t.getTime();
}

function parts(iso: string): [number, number, number] | null {
  const m = ISO_DATE.exec(iso);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (y < 1) return null;
  const t = new Date(utcMs(y, mo, d));
  if (t.getUTCFullYear() !== y || t.getUTCMonth() !== mo - 1 || t.getUTCDate() !== d) return null;
  return [y, mo, d];
}

const invalid = (v: unknown): never => {
  throw new DomainError("invalid_date", `data inválida: ${String(v)}`);
};

function toUtcMs(iso: string): number {
  const p = parts(iso) ?? invalid(iso);
  return utcMs(p[0], p[1], p[2]);
}

const pad = (n: number, w = 2) => String(n).padStart(w, "0");
/** Fora dos anos 0001–9999 (inclui NaN, além do alcance do Date) a data não cabe em AAAA-MM-DD: invalid_date. */
const fromUtcMs = (ms: number) => {
  const t = new Date(ms);
  const y = t.getUTCFullYear();
  if (!(y >= 1 && y <= 9999)) invalid(ms);
  return `${pad(y, 4)}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
};

export const isIsoDate = (s: string): boolean => parts(s) !== null;

export function addDays(iso: string, days: number): string {
  if (!Number.isSafeInteger(days)) invalid(days);
  return fromUtcMs(toUtcMs(iso) + days * DAY_MS);
}

/** 0 = domingo … 6 = sábado. */
export const weekday = (iso: string): number => new Date(toUtcMs(iso)).getUTCDay();

/** dd/mm/aaaa (padrão da UI). */
export function formatDateBR(iso: string): string {
  const p = parts(iso) ?? invalid(iso);
  return `${pad(p[2])}/${pad(p[1])}/${pad(p[0], 4)}`;
}

const SP_DATE = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Sao_Paulo",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** Data de hoje no fuso America/Sao_Paulo (o "en-CA" já formata como AAAA-MM-DD). */
export const todayInSaoPaulo = (now: Date = new Date()): string => SP_DATE.format(now);

/** Domingo de Páscoa (calendário gregoriano, algoritmo de Meeus/Jones/Butcher). */
export function easterSunday(year: number): string {
  if (!Number.isSafeInteger(year) || year < 1583 || year > 9999) invalid(year);
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return `${pad(year, 4)}-${pad(month)}-${pad(day)}`;
}

const FIXED_HOLIDAYS = ["01-01", "04-21", "05-01", "09-07", "10-12", "11-02", "11-15", "12-25"];

/**
 * Feriados nacionais (Lei 662/1949, Lei 6.802/1980, Lei 14.759/2023): os fixos, a Sexta-feira Santa e o Dia da
 * Consciência Negra (20/11) a partir de 2024. Carnaval e Corpus Christi são ponto facultativo e ficam fora.
 */
export function nationalHolidays(year: number): string[] {
  const list = FIXED_HOLIDAYS.map((md) => `${pad(year, 4)}-${md}`);
  list.push(addDays(easterSunday(year), -2));
  if (year >= 2024) list.push(`${pad(year, 4)}-11-20`);
  return list.sort();
}

const holidayCache = new Map<number, ReadonlySet<string>>();
const holidaysOf = (year: number) => {
  let s = holidayCache.get(year);
  if (!s) {
    s = new Set(nationalHolidays(year));
    holidayCache.set(year, s);
  }
  return s;
};

export function isBusinessDay(iso: string): boolean {
  const wd = weekday(iso);
  if (wd === 0 || wd === 6) return false;
  return !holidaysOf(Number(iso.slice(0, 4))).has(iso);
}

/** A própria data se já for dia útil; senão o próximo dia útil. */
export function nextBusinessDay(iso: string): string {
  let d = iso;
  while (!isBusinessDay(d)) d = addDays(d, 1);
  return d;
}
